<template>
  <div class="py-4 px-6">
    <v-card-title class="pb-0 mb-0">
      <span class="units-table-title">Cupons por loja</span>
      <v-spacer />
      <export-data :data-fetcher="getDataToExport" filename="resultados_cupons_por_loja" ghost />
      <section class="d-flex align-center justify-end">
        <headers-data-table-settings
          :key="headersSettingsName"
          :headers.sync="headers"
          :default-headers="getDefaultHeadersOptions()"
          :headers-settings-name="headersSettingsName"
        />
      </section>
      <div class="min-width-345">
        <mf-search v-model="search" class="pt-0 mt-0" hide-details placeholder="Buscar por loja" />
      </div>
    </v-card-title>

    <v-card-subtitle class="text-cinza-d10">Desdobramento do funil de cupons em cada loja</v-card-subtitle>
    <v-data-table
      class="units-table"
      :headers="computedHeaders"
      :items="items"
      :search="search"
      :footer-props="footerProps"
      sort-by="coupons_eligible_percent"
      sort-desc
    >
      <template v-for="h in headers" v-slot:[`header.${h.value}`]="{ header }">
        <mf-tooltip v-bind:key="h.text" top max-width="192px" :disabled="!h.tooltip">
          <template v-slot:activator="{ on }">
            {{ h.text }}
            <mf-icon v-show="h.tooltip" small color="grey" v-on="on">info</mf-icon>
          </template>
          <span>{{ h.tooltip }}</span>
        </mf-tooltip>
      </template>

      <template v-slot:item.unit_name="{ item }">
        <span class="units-table-unit-name">{{ item.unit_name }}</span>
      </template>

      <template v-slot:item.coupons_eligible_percent="{ item }">
        <percent-color-chip :value="item.coupons_eligible_percent" :highest-value="couponsEligibleHighestValue" :offer-type="offerType" />
      </template>

      <template v-slot:item.coupons_trigger_percent="{ item }">
        <percent-color-chip :value="item.coupons_trigger_percent" :highest-value="couponsTriggerHighestValue" :offer-type="offerType" />
      </template>

      <template v-slot:item.coupons_activated_percent="{ item }">
        <percent-color-chip :value="item.coupons_activated_percent" :highest-value="couponsActivatedHighestValue" :offer-type="offerType" />
      </template>

      <template v-slot:item.burn_percent="{ item }">
        <percent-color-chip :value="item.burn_percent" :highest-value="couponsBurnHighestValue" :offer-type="offerType" />
      </template>
    </v-data-table>
  </div>
</template>

<script>
import PercentColorChip from './PercentColorChip.vue'
import cloneDeep from 'lodash/cloneDeep'
import ExportData from '@/components/export-data/ExportData.vue'
import HeadersDataTableSettings from '@/components/HeadersDataTableSettings.vue'

export default {
  name: 'CouponsPerUnitTable',
  components: {
    PercentColorChip,
    ExportData,
    HeadersDataTableSettings,
  },
  props: {
    metrics: {
      type: Array,
      default(_) {
        return []
      },
    },
    useEarnMetrics: {
      type: Boolean,
      default: false,
    },
    offerType: {
      type: String,
      validator: (value) => ['takepay', 'nextbuycashback', 'dynamic_rule', 'general', 'crosssell', 'percentage'].includes(value),
    },
  },
  data: () => ({
    search: '',
    headers: [],
  }),
  computed: {
    defaultHeaders() {
      const headers = [
        {
          text: 'Loja',
          align: 'start',
          sortable: true,
          filterable: true,
          width: '20%',
          value: 'unit_name',
          isActive: true,
          isRequired: true,
          isDefault: true,
        },
        {
          text: 'Com o produto',
          align: 'center',
          sortable: true,
          value: 'coupons_with_product',
          tooltip: this.isCrossSell
            ? 'Total de cupons do período da oferta que tem ao menos um dos SKU gatilho da oferta.'
            : 'Total de cupons do período da oferta que tem ao menos um dos SKU participantes da oferta.',
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: 'Com quantidade elegível',
          align: 'center',
          sortable: true,
          value: 'coupons_eligible',
          tooltip: this.isCrossSell
            ? 'Cupons que atingiram a quantidade mínima de produtos gatilho da oferta.'
            : 'Cupons que cumpriram as regras da oferta, respeitando quantidade mínima e máxima.',
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: 'Com quantidade elegível (%)',
          align: 'center',
          sortable: true,
          value: 'coupons_eligible_percent',
          tooltip: this.isCrossSell ? '% de cupons que atingiram a quantidade mínima de produtos gatilho.' : '% de cupons que cumpriram os critérios da oferta',
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: 'Ativaram a oferta',
          align: 'center',
          sortable: true,
          value: 'coupons_activated',
          tooltip: 'Cupons que cumpriram as regras e o cliente aceitou o benefício',
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: 'Ativaram a oferta (%)',
          align: 'center',
          sortable: true,
          value: 'coupons_activated_percent',
          tooltip: '% de cupons que o cliente aceitou o benefício',
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
      ]

      if (this.isNextbuyCashback || this.isDigitalVoucher) {
        headers.push(
          {
            text: 'Utilizaram o benefício',
            align: 'center',
            sortable: true,
            value: 'burn',
            isActive: true,
            isRequired: false,
            isDefault: true,
          },
          {
            text: 'Utilizaram o benefício (%)',
            align: 'center',
            sortable: true,
            value: 'burn_percent',
            tooltip: '% de cupons que utilizaram o benefício',
            isActive: true,
            isRequired: false,
            isDefault: true,
          }
        )
      }

      if (this.isCrossSell) {
        headers.splice(4, 1, {
          text: 'Prod. gatilho + prod. com desconto (un.)',
          align: 'center',
          sortable: true,
          value: 'coupons_trigger',
          tooltip: 'Cupons que cumpriram o mínimo de ao menos um dos produtos gatilho e possuem o produto alvo.',
          isActive: false,
          isRequired: false,
          isDefault: false,
        })
        headers.splice(5, 1, {
          text: 'Prod. gatilho + prod. com desconto (%)',
          align: 'center',
          sortable: true,
          value: 'coupons_trigger_percent',
          tooltip: '% dos cupons que cumpriram o mínimo de ao menos um dos produtos gatilho e possuem o produto alvo.',
          isActive: true,
          isRequired: false,
          isDefault: true,
        })
        headers.push({
          text: 'Com desconto aplicado (un.)',
          align: 'center',
          sortable: true,
          value: 'coupons_activated',
          tooltip: 'Cupons que cumpriram as regras e o cliente aceitou o benefício.',
          isActive: false,
          isRequired: false,
          isDefault: false,
        })
        headers.push({
          text: 'Com desconto aplicado (%)',
          align: 'center',
          sortable: true,
          value: 'coupons_activated_percent',
          tooltip: '% de cupons que cumpriram as regras e o cliente aceitou o benefício.',
          isActive: true,
          isRequired: false,
          isDefault: true,
        })
      }

      return headers
    },

    items() {
      const items = []
      if (!this.metrics) return items

      for (const unit of this.metrics) {
        const unitMetricsOfferType = unit.metrics?.[this.offerType]
        items.push({
          unit_name: unit.unit_name,
          coupons_with_product: unitMetricsOfferType?.products_metrics?.totals?.total.tickets || 0,
          coupons_eligible: unitMetricsOfferType?.eligible_metrics?.tickets || 0,
          coupons_activated: (this.useEarnMetrics ? this.metrics?.earn_metrics?.tickets : unitMetricsOfferType?.clients_metrics?.with_sales?.tickets) || 0,
          burn: unitMetricsOfferType?.burn_metrics?.tickets || 0,
          coupons_trigger: unitMetricsOfferType?.eligible_with_target_metrics?.tickets || 0,
        })
      }

      const calculatedItems = []
      for (let item of items) {
        calculatedItems.push({
          ...item,
          coupons_eligible_percent: this.calcPercent(item.coupons_with_product, item.coupons_eligible),
          coupons_activated_percent: this.isCrossSell
            ? this.calcPercent(item.coupons_trigger, item.coupons_activated)
            : this.calcPercent(item.coupons_eligible, item.coupons_activated),
          burn_percent: this.calcPercent(item.coupons_activated, item.burn),
          coupons_trigger_percent: this.calcPercent(item.coupons_eligible, item.coupons_trigger),
        })
      }
      return calculatedItems
    },

    computedHeaders() {
      return this.headers.filter((header) => header.isActive)
    },

    footerProps() {
      return { itemsPerPageOptions: [10, 15, 20] }
    },

    couponsEligibleHighestValue() {
      return this.calcHighestValue('coupons_eligible_percent')
    },

    couponsTriggerHighestValue() {
      return this.calcHighestValue('coupons_trigger_percent')
    },

    couponsActivatedHighestValue() {
      return this.calcHighestValue('coupons_activated_percent')
    },

    couponsBurnHighestValue() {
      return this.calcHighestValue('burn_percent')
    },

    isNextbuyCashback() {
      return this.offerType === 'nextbuycashback'
    },

    isDigitalVoucher() {
      return this.offerType === 'dynamic_rule'
    },

    isCrossSell() {
      return this.offerType === 'crosssell'
    },

    headersSettingsName() {
      if (this.isNextbuyCashback) return 'coupons-per-unit-th-cashback'
      else if (this.isDigitalVoucher) return 'coupons-per-unit-th-voucher'
      else if (this.isCrossSell) return 'coupons-per-unit-th-cross-sell'
      else return 'coupons-per-unit-th'
    },
  },
  methods: {
    calcPercent(value1, value2) {
      return value1 > 0 ? (value2 * 100) / value1 : 0
    },

    calcHighestValue(property) {
      const coupons = []
      for (const item of this.items) {
        coupons.push(item[property])
      }

      coupons.sort((a, b) => b - a)
      return coupons[0]
    },

    getDataToExport() {
      const headers = this.computedHeaders.map((header) => header.text)
      const rows = this.items.map((item) => this.computedHeaders.map((header) => item[header.value]))
      return { headers, rows }
    },

    getDefaultHeadersOptions() {
      return cloneDeep(this.defaultHeaders)
    },
  },
}
</script>

<style lang="scss" scoped>
.units-table-title {
  color: $cinza-d10;
  font-size: 20px;
}

.units-table-unit-name {
  color: $cinza-d10;
  font-weight: bold;
}

.min-width-345 {
  min-width: 345px;
}

::v-deep th {
  color: $cinza-d20 !important;
  font-size: 14px;
  font-weight: bold;
}

::v-deep th:hover {
  opacity: 0.8;
}

::v-deep tbody {
  td {
    color: $cinza-0;
    font-size: 14px;
    line-height: 1;
  }
}

::v-deep tr:nth-child(even) {
  background-color: #f7f7f7;
}
</style>
